import styled from 'styled-components';

export const ToolBuilderShell = styled.div`
  width: 100%;
  max-width: 56rem;
  margin: 0 auto;
  color: var(--ink);
`;

export const ToolSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const ToolSection = styled.section`
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: var(--shadow);
  color: var(--ink);
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  h3 {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    color: var(--ink);
  }

  p {
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 18px;
    color: var(--quiet);
  }
`;

export const FieldBlock = styled.div`
  min-width: 0;

  label {
    display: block;
    margin: 0 0 6px;
    font-size: 14px;
    line-height: 18px;
    font-weight: 600;
    color: var(--ink);

    .req {
      color: var(--danger);
      margin-right: 2px;
    }
  }
`;

const fieldBase = `
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: var(--card);
  color: var(--ink);
  font-size: 14px;
  line-height: 18px;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &::placeholder {
    color: var(--quiet);
  }

  &:focus {
    border-color: var(--o1);
    box-shadow: 0 0 0 3px var(--o-halo);
  }
`;

export const ToolInput = styled.input`
  ${fieldBase}
`;

export const ToolTextarea = styled.textarea`
  ${fieldBase}
  line-height: 20px;
  resize: none;
`;

export const ToolSelect = styled.select`
  ${fieldBase}

  option {
    background: var(--card);
    color: var(--ink);
  }
`;

export const FieldGrid = styled.div`
  display: grid;
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: ${({ $cols }) => ($cols === 4 ? '3fr 1fr' : '1fr 1fr')};
  }
`;

export const DefCard = styled.div`
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--card-warm);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const DefCardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  span {
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: var(--quiet);
  }

  button {
    padding: 4px;
    border: none;
    background: transparent;
    color: var(--quiet);
    cursor: pointer;

    &:hover {
      color: var(--danger);
    }
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  button {
    flex-shrink: 0;
    padding: 4px;
    border: none;
    background: transparent;
    color: var(--quiet);
    cursor: pointer;

    &:hover {
      color: var(--danger);
    }
  }
`;

export const AddRowBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: var(--o2);
  font-size: 12px;
  line-height: 16px;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;

  &:hover {
    color: var(--o-deep);
    text-decoration: underline;
  }
`;

export const EmptyHint = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--quiet);
`;

export const MetaCard = styled.div`
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--card-warm);
  padding: 12px;

  .label {
    margin: 0 0 4px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: var(--quiet);
  }

  .value {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    color: var(--ink);
    word-break: break-all;
  }
`;

export const MetaGrid = styled.div`
  display: grid;
  gap: 12px;

  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
  }
`;

export const OutputBox = styled.pre`
  margin: 0;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: var(--card-warm);
  color: var(--fg-2);
  font-size: 12px;
  line-height: 18px;
  white-space: pre-wrap;
  min-height: 64px;
  max-height: 256px;
  overflow-y: auto;
`;

export const MonacoWrap = styled.div`
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
  background: var(--card);
`;

export const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
`;

export const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  .loading-row {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    color: var(--quiet);

    svg {
      animation: tool-spin 0.8s linear infinite;
      color: var(--o2);
    }
  }

  @keyframes tool-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .skeleton {
    background: var(--card);
    border: 1px solid var(--line);
    border-radius: 16px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    box-shadow: var(--shadow);

    .line {
      height: 16px;
      width: 128px;
      border-radius: 8px;
      background: var(--band);
    }

    .grid {
      display: grid;
      gap: 16px;

      @media (min-width: 768px) {
        grid-template-columns: 1fr 1fr;
      }

      .box {
        height: 36px;
        border-radius: 10px;
        background: var(--band);
      }
    }

    .area {
      height: 80px;
      border-radius: 10px;
      background: var(--band);
    }
  }
`;

export const InlineActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;
